class SizeSelect extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.trigger = this.querySelector('.size-select__trigger');
    this.list = this.querySelector('.size-select__list');
    this.select = this.querySelector('select');
    this.options = Array.from(this.querySelectorAll('.size-select__option'));
    this.optionPosition = Number(this.dataset.optionPosition) - 1;
    this.variantSelects = this.closest('variant-selects');

    try {
      this.variants = JSON.parse(this.querySelector('[data-size-select-variants]').textContent);
      this.moneyFormat = JSON.parse(this.querySelector('[data-size-select-money-format]').textContent);
    } catch (error) {
      console.warn('SizeSelect could not read variant pricing data.', error);
      this.variants = [];
      this.moneyFormat = '${{amount}}';
    }

    this.trigger.addEventListener('click', () => this.toggle());
    this.trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event));
    this.options.forEach((option) => {
      option.addEventListener('click', () => this.selectValue(option.dataset.value));
      option.addEventListener('keydown', (event) => this.onOptionKeydown(event, option));
    });
    this.select.addEventListener('change', () => this.sync());
    document.addEventListener('click', this.onDocumentClick);
    this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  onDocumentClick = (event) => {
    if (!this.contains(event.target)) this.close();
  };

  toggle() {
    this.list.hidden ? this.open() : this.close();
  }

  open() {
    this.updatePrices();
    this.list.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
  }

  close({ restoreFocus = false } = {}) {
    if (this.list.hidden) return;
    this.list.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
    if (restoreFocus) this.trigger.focus();
  }

  selectValue(value) {
    if (this.select.value !== value) {
      this.select.value = value;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.sync();
    this.close({ restoreFocus: true });
  }

  sync() {
    const value = this.select.value;
    const variant = this.findVariant(value);
    this.querySelector('.size-select__value').textContent = value;
    this.querySelector('.size-select__price').textContent = variant ? this.formatMoney(variant.price) : '';
    this.options.forEach((option) => {
      const selected = option.dataset.value === value;
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-selected', String(selected));
    });
    this.updatePrices();
  }

  updatePrices() {
    this.options.forEach((option) => {
      const variant = this.findVariant(option.dataset.value);
      option.querySelector('[data-size-price]').textContent = variant ? this.formatMoney(variant.price) : '';
      option.disabled = !variant || !variant.available;
    });
  }

  findVariant(sizeValue) {
    const selectedValues = Array.from(
      this.variantSelects.querySelectorAll('.product-form__input')
    ).map((input) => {
      const select = input.querySelector('select');
      if (select) return select.value;
      return input.querySelector('input[type="radio"]:checked')?.value || '';
    });
    selectedValues[this.optionPosition] = sizeValue;
    return this.variants.find((variant) =>
      selectedValues.every((value, index) => !value || variant.options[index] === value)
    );
  }

  formatMoney(cents) {
    const amount = (cents / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return this.moneyFormat
      .replace(/{{\s*amount_with_comma_separator\s*}}/, amount.replace('.', ','))
      .replace(/{{\s*amount_no_decimals\s*}}/, String(Math.round(cents / 100)))
      .replace(/{{\s*amount\s*}}/, amount);
  }

  onTriggerKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.open();
      const selected = this.options.find((option) => option.getAttribute('aria-selected') === 'true');
      (selected || this.options[0])?.focus();
    }
  }

  onOptionKeydown(event, option) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close({ restoreFocus: true });
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.selectValue(option.dataset.value);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const enabledOptions = this.options.filter((item) => !item.disabled);
    const currentIndex = enabledOptions.indexOf(option);
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (currentIndex + direction + enabledOptions.length) % enabledOptions.length;
    enabledOptions[nextIndex]?.focus();
  }
}

if (!customElements.get('size-select')) {
  customElements.define('size-select', SizeSelect);
}
